import CommonFeatureStructure from "../Constants/ExploreFeatureStructure"; // Import structure

/**
 * Fetch feature data from mock JSON
 * @returns {Promise<Object>} Formatted feature data following CommonFeatureStructure
 */
export const fetchFeatureData = async () => {
  try {
    const response = await fetch("/api/mock/AccountPage/UserAccountExploreFeaturesMockapi.json"); // Fetch from public folder
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();

    // Ensure data follows CommonFeatureStructure
    const formattedData = {
      featureList: (data.featureList || []).map((feature) => ({
        featureId: feature.featureId || 0,
        featureUuid: feature.featureUuid || "",
        featureName: feature.featureName || "",
        featureCode: feature.featureCode || "",
        title: feature.title || "",
        description: feature.description || "",
        image: feature.image || "",
        videoUrl: feature.videoUrl || "",
        createdOn: feature.createdOn || "",
        createdBy: feature.createdBy || "",
        category: feature.category || "",
        isDefault: feature.isDefault || false
      }))
    };

    return formattedData;
  } catch (error) {
    console.error("Error fetching feature data:", error);
    
    // Return empty structure on error
    return { featureList: [] };
  }
};

/**
 * Get selected features from localStorage
 * @returns {Array<string>} Array of selected feature codes
 */
export const getSelectedFeatures = () => {
  try {
    const saved = localStorage.getItem('features');
    if (!saved) return ['LS']; // Default to LS
    
    const parsed = JSON.parse(saved);
    const features = Array.isArray(parsed) ? parsed : ['LS'];
    
    // Ensure LS is always included
    if (!features.includes('LS')) {
      features.push('LS');
    }
    
    return features;
  } catch (error) {
    console.error('Error reading selected features:', error);
    return ['LS'];
  }
};

/**
 * Save selected features to localStorage
 * @param {Array<string>} featureCodes - Array of feature codes to save
 * @returns {boolean} Success status
 */
export const saveSelectedFeatures = (featureCodes) => {
  try {
    // Ensure LS is always included
    const features = featureCodes.includes('LS') 
      ? featureCodes 
      : ['LS', ...featureCodes];
    
    localStorage.setItem('features', JSON.stringify(features));
    return true;
  } catch (error) {
    console.error('Error saving selected features:', error);
    return false;
  }
};

/**
 * Toggle feature selection
 * @param {string} featureCode - Feature code to toggle
 * @param {Array<string>} currentSelection - Current selected features
 * @returns {Array<string>} Updated selected features
 */
export const toggleFeatureSelection = (featureCode, currentSelection) => {
  // LS cannot be deselected
  if (featureCode === 'LS') {
    return currentSelection;
  }
  
  if (currentSelection.includes(featureCode)) {
    return currentSelection.filter(code => code !== featureCode);
  } else {
    return [...currentSelection, featureCode];
  }
};

/**
 * Format date for display
 * @param {string} isoDateString - ISO 8601 date string
 * @returns {string} Formatted date string
 */
export const formatFeatureDate = (isoDateString) => {
  try {
    return new Date(isoDateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Get feature by code
 * @param {Array<Object>} featureList - List of features
 * @param {string} featureCode - Feature code to find
 * @returns {Object|null} Feature object or null
 */
export const getFeatureByCode = (featureList, featureCode) => {
  return featureList.find(feature => feature.featureCode === featureCode) || null;
};

/**
 * Get features by category
 * @param {Array<Object>} featureList - List of features
 * @param {string} category - Category to filter by
 * @returns {Array<Object>} Filtered features
 */
export const getFeaturesByCategory = (featureList, category) => {
  return featureList.filter(feature => feature.category === category);
};

/**
 * Get default features
 * @param {Array<Object>} featureList - List of features
 * @returns {Array<Object>} Default features
 */
export const getDefaultFeatures = (featureList) => {
  return featureList.filter(feature => feature.isDefault === true);
};